import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { UserReachabilityStatus } from 'models/twilio';
import { COLORS, POSITIONING } from '../constants';
import useTwilioTitle from '../hooks/useTwilioTitle';
import UserTag from './styled/UserTag';
import UserAvatar from './styled/UserAvatar';
import { ChatBackButton, ChatCloseButton } from './CommonChatButtons';
import { MemoedChatBadge } from './styled/ChatBadge';
import OnlineDot from './styled/OnlineDot';
import {
  ChatTitleSubtitleGroupContainer,
  ChatTitleText,
  ChatTitleContainer,
  ChatSubtitleText,
} from './CommonChatDisplayComponents';
import ChatTitleButtonGroup from './styled/ChatTitleButtonGroup';
import TRANSLATIONS from '../translations';

const ChatInterfaceTitleContainer = styled.div`
  display: flex;
  padding: 16px 12px;
  font-family: Lato, sans-serif;
  background-color: ${COLORS.GREY_S};
  color: ${COLORS.TEXT_DARK};
  align-items: center;
  flex-shrink: 0;
  height: ${POSITIONING.TITLE_HEIGHT};
`;

const StyledTitleSubtitleGroupContainer = styled(ChatTitleSubtitleGroupContainer)`
  flex-grow: 0;
  margin-left: 12px;
`;

const StyledTitleText = styled(ChatTitleText)`
  font-size: 15px;
`;

const TitleDisplayContainer = styled.div<{ $mobile: boolean }>`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: ${({ $mobile }) => ($mobile ? 'center' : 'flex-start')};
  overflow: hidden;
`;

const MobileButtonGroupContainer = styled(ChatTitleButtonGroup)`
  pointer-events: all;
`;

const OnlineIndicatorContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  color: #44B700;
  line-height: 16px;
`;

const OnlineDotContainer = styled.div`
  margin: auto 8px;
  flex: 1;
`;

export interface ChatInterfaceTitleProps {
  mobile: boolean;
}

const OnlineIndicator = () => {
  const { t } = useTranslation();
  return (
    <OnlineIndicatorContainer>
      <OnlineDotContainer>
        <OnlineDot />
      </OnlineDotContainer>
      <div>
        {t(TRANSLATIONS.STATE.ONLINE[0], TRANSLATIONS.STATE.ONLINE[1])}
      </div>
    </OnlineIndicatorContainer>
  );
};

const ChatInterfaceTitle: FC<ChatInterfaceTitleProps> = ({ mobile }: ChatInterfaceTitleProps) => {
  const {
    chatName, userTag, userDescription, iconSrc, buttonGroup, reachabilityStatus,
  } = useTwilioTitle();

  const TitleDisplay = useMemo(() => (
    <TitleDisplayContainer $mobile={mobile}>
      {
        reachabilityStatus === UserReachabilityStatus.ONLINE && mobile ? (
          <MemoedChatBadge displayOnlineDot>
            <UserAvatar src={iconSrc} name={chatName} />
          </MemoedChatBadge>
        ) : (
          <UserAvatar src={iconSrc} name={chatName} />
        )
      }
      <StyledTitleSubtitleGroupContainer>
        <ChatTitleContainer id="chat-title-name-container">
          <StyledTitleText id="chat-title-text">{chatName}</StyledTitleText>
          {userTag && <UserTag id="chat-title-tag">{userTag}</UserTag>}
        </ChatTitleContainer>
        {userDescription && (
          <ChatSubtitleText id="chat-title-description">
            {userDescription}
          </ChatSubtitleText>
        )}
      </StyledTitleSubtitleGroupContainer>
      {reachabilityStatus === UserReachabilityStatus.ONLINE && !mobile && <OnlineIndicator />}
    </TitleDisplayContainer>
  ), [chatName, iconSrc, mobile, userDescription, userTag, reachabilityStatus]);

  const MobileButtonGroup = useMemo(() => (
    <MobileButtonGroupContainer>
      <ChatBackButton id="mobile-chat-back-button" />
      <ChatCloseButton id="mobile-chat-close-button" />
    </MobileButtonGroupContainer>
  ), []);

  return (
    <ChatInterfaceTitleContainer id="chat-interface-title-container">
      {mobile && MobileButtonGroup}
      {TitleDisplay}
      {buttonGroup()}
    </ChatInterfaceTitleContainer>
  );
};

export default React.memo(ChatInterfaceTitle);
